import { useEffect, useMemo, useState } from "react";
import { IconCategory, IconPlus, IconSearch } from "@tabler/icons-react";
import { Button, Card, Group, Heading, Input, PaginadorTabla, Stack, Text, ThemeIcon } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useCategoriasDocumentoAdmin } from "../hooks/use-categorias-documento-admin";
import { CategoriaDocumentoForm } from "./CategoriaDocumentoForm";
import { CategoriasDocumentoTable } from "./CategoriasDocumentoTable";
import { listarCategoriasDocumento, type CategoriaDocumentoDto } from "../services/categorias-documento-service";

const PAGE_SIZE = 15;

/**
 * Página /admin/categorias-documento · listado + CRUD del catálogo
 * administrable de categorías de documento. Búsqueda y paginado se
 * resuelven en el SERVIDOR (query params con debounce de 350ms).
 *
 * Las categorías de sistema (FacturaXml, FacturaPdf, Evidencia, Contrato,
 * Adicional · sembradas por el seed) sólo permiten editar metadata visible.
 * Categorías nuevas se pueden crear pero requieren código en el enum
 * backend antes de ser usables.
 */
export function AdminCategoriasDocumentoPage() {
  const [editando, setEditando] = useState<CategoriaDocumentoDto | "nueva" | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const filtrosServer = useMemo(
    () => (busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
    [busquedaDebounced]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useCategoriasDocumentoAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const categorias = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodas = async (): Promise<CategoriaDocumentoDto[]> => {
    const todas: CategoriaDocumentoDto[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarCategoriasDocumento({ ...filtrosServer, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Group gap="sm">
            <ThemeIcon variant="filled" color="multiva" size="lg" radius="md">
              <IconCategory size={20} stroke={1.5} />
            </ThemeIcon>
            <Stack gap={2}>
              <Heading order={2}>Categorías de documento</Heading>
              <Text size="sm" c="dimmed">
                {data?.totalCatalogo ?? 0} categorías · {data?.totalActivas ?? 0} activas · {data?.totalSistema ?? 0} de sistema
              </Text>
            </Stack>
          </Group>
          <Button leftSection={<IconPlus size={14} />} onClick={() => setEditando("nueva")} disabled={editando !== null}>
            Nueva categoría
          </Button>
        </Group>

        {editando === "nueva" ? (
          <CategoriaDocumentoForm onSalir={() => setEditando(null)} />
        ) : editando ? (
          <CategoriaDocumentoForm categoria={editando} onSalir={() => setEditando(null)} />
        ) : null}

        <Card padding="lg">
          <Stack gap="md">
            <Input
              label="Buscar"
              placeholder="código, nombre o descripción…"
              leftSection={<IconSearch size={14} stroke={1.7} />}
              value={busqueda}
              onChange={(e) => setBusqueda(e.currentTarget.value)}
              wrapperClassName="max-w-90"
            />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <CategoriasDocumentoTable categorias={categorias} onEditar={(cat) => setEditando(cat)} obtenerFilas={obtenerTodas} />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="categorías" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
